import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AspiringLayout from "@/components/AspiringLayout";
import { LaunchTab } from "@/components/aspiring-tabs/LaunchTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LAUNCH_FIELDS = ["current_employment", "months_runway", "risk_tolerance", "next_steps"];

function LaunchContent({ user }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [profile, setProfile] = useState({});

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/aspiring/profile`, { credentials: "include" })
      .then(r => r.ok ? r.json() : {})
      .then(d => setProfile(d.profile || {}))
      .finally(() => setLoading(false));
  }, []);

  const save = async (data = profile) => {
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/aspiring/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data)
      });
      toast[r.ok ? "success" : "error"](r.ok ? "Saved!" : "Failed");
    } catch {
      toast.error("Failed");
    }
    setSaving(false);
  };

  const clear = async () => {
    if (!confirm("Are you sure you want to clear this section?")) return;
    setClearing(true);
    const clearedProfile = { ...profile };
    LAUNCH_FIELDS.forEach(f => { clearedProfile[f] = f === "next_steps" ? null : ""; });
    setProfile(clearedProfile);
    await save(clearedProfile);
    toast.success("Section cleared");
    setClearing(false);
  };

  const update = (k, v) => setProfile(p => ({ ...p, [k]: v }));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="aspiring-launch-page">
      <LaunchTab profile={profile} update={update} />
      <div className="flex gap-3">
        <Button 
          onClick={clear} 
          disabled={clearing || saving} 
          variant="outline"
          className="flex-1 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
          data-testid="clear-launch-btn"
        >
          {clearing && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          <Trash2 className="w-4 h-4 mr-2" />
          Clear
        </Button>
        <Button 
          onClick={() => save()} 
          disabled={saving || clearing} 
          className="flex-1 bg-amber-500 hover:bg-amber-600"
          data-testid="save-launch-btn"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          <Save className="w-4 h-4 mr-2" />
          Save
        </Button>
      </div>
    </div>
  );
}

export default function AspiringLaunchPage() {
  return <AspiringLayout>{({ user }) => <LaunchContent user={user} />}</AspiringLayout>;
}
